import {Model} from '@geckos.io/typed-array-buffer-schema';

export class InvalidMultiplayerPacketError extends Error {}

const numberReaders = {
	Uint8Array: 'getUint8',
	Int16Array: 'getInt16',
	Int32Array: 'getInt32',
};

const makeLayout = schema => {
	const fields = [], arrays = [];
	for (const [name, prop] of Object.entries(schema.struct).sort(([a], [b]) => a.localeCompare(b))) {
		if (Array.isArray(prop)) {
			arrays.push({name, layout: makeLayout(prop[0])});
			continue;
		}
		const type = prop.type || prop;
		const string = type._type === 'String8' || type._type === 'String16';
		const read = string ? (type._type === 'String16' ? 'getUint16' : 'getUint8') : numberReaders[type._type];
		if (!read) throw new Error(`Unsupported multiplayer field type: ${type._type}`);
		fields.push({name, read, write: read.replace('get', 'set'), string, length: string ? (prop.length || 12) : 1, bytes: type._bytes, digits: prop.digits, factor: 10 ** (prop.digits || 0)});
	}
	return {id: schema.id, fields, arrays, bytes: fields.reduce((sum, field) => sum + field.bytes * field.length, 0)};
};

// Keep the existing wire IDs and layout. The library's decoder mutates field
// definitions (_type/_bytes), which makes subsequent encoding ignore string
// lengths and fall back to 12. It also scans payload bytes for schema IDs.
// Read the known fields at record boundaries instead, without changing schemas.
// Compile the layout once for encoding too: no sorting, JSON clone, oversized
// scratch buffer or byte-by-byte buffer copy in the 20 Hz pose send path.
export class MultiplayerModel extends Model {
	constructor(schema, arrayLengths = {}) {
		super(schema);
		this.layout = makeLayout(schema);
		this.arrayLengths = arrayLengths;
		let variants = [{bytes: schema.id.length + this.layout.bytes, counts: {}}];
		for (const array of this.layout.arrays) {
			if (array.layout.arrays.length || !arrayLengths[array.name]) throw new Error(`Missing multiplayer array lengths: ${array.name}`);
			variants = variants.flatMap(variant => arrayLengths[array.name].map(count => ({
				bytes: variant.bytes + array.layout.id.length + count * array.layout.bytes,
				counts: {...variant.counts, [array.name]: count},
			})));
		}
		this.arrayCountsBySize = new Map(variants.map(variant => [variant.bytes, variant.counts]));
		if (this.arrayCountsBySize.size !== variants.length) throw new Error('Ambiguous multiplayer packet lengths');
	}

	toBuffer(state) {
		const layout = this.layout;
		let bytes = layout.id.length + layout.bytes;
		for (const array of layout.arrays) {
			const values = state[array.name];
			if (!Array.isArray(values) || !this.arrayLengths[array.name].includes(values.length)) {
				throw new InvalidMultiplayerPacketError(`Invalid multiplayer array: ${array.name}`);
			}
			bytes += array.layout.id.length + values.length * array.layout.bytes;
		}
		const buffer = new ArrayBuffer(bytes);
		const view = new DataView(buffer);
		let offset = 0;
		const writeHeader = id => {
			for (let i = 0; i < id.length; i++) view.setUint8(offset++, id.charCodeAt(i));
		};
		const writeFields = (record, data) => {
			for (const field of record.fields) {
				const value = data[field.name];
				if (field.string) {
					for (let i = 0; i < field.length; i++) {
						view[field.write](offset, i < value.length ? value.charCodeAt(i) : 32);
						offset += field.bytes;
					}
				} else {
					view[field.write](offset, field.digits ? Number((value * field.factor).toFixed(0)) : value);
					offset += field.bytes;
				}
			}
		};
		writeHeader(layout.id);
		writeFields(layout, state);
		for (const array of layout.arrays) {
			writeHeader(array.layout.id);
			for (const value of state[array.name]) writeFields(array.layout, value);
		}
		return buffer;
	}

	fromBuffer(buffer) {
		if (!(buffer instanceof ArrayBuffer)) throw new InvalidMultiplayerPacketError('Expected an ArrayBuffer');
		const arrayCounts = this.arrayCountsBySize.get(buffer.byteLength);
		if (!arrayCounts) throw new InvalidMultiplayerPacketError('Invalid multiplayer packet length');
		const view = new DataView(buffer);
		let offset = 0;
		const requireBytes = count => {
			if (offset + count > view.byteLength) throw new InvalidMultiplayerPacketError('Incomplete multiplayer packet');
		};
		const matchesHeader = id => {
			if (offset + id.length > view.byteLength) return false;
			for (let i = 0; i < id.length; i++) if (view.getUint8(offset + i) !== id.charCodeAt(i)) return false;
			return true;
		};
		const readHeader = id => {
			if (!matchesHeader(id)) throw new InvalidMultiplayerPacketError('Unexpected multiplayer record');
			offset += id.length;
		};
		const readRecord = layout => {
			const result = {};
			for (const field of layout.fields) {
				requireBytes(field.bytes * field.length);
				let value;
				if (field.string) {
					value = '';
					for (let i = 0; i < field.length; i++) {
						value += String.fromCharCode(view[field.read](offset));
						offset += field.bytes;
					}
				} else {
					value = view[field.read](offset);
					offset += field.bytes;
					if (field.digits) value = Number((value * 10 ** -field.digits).toFixed(field.digits));
				}
				result[field.name] = value;
			}
			// The legacy format has an array header followed by packed records.
			// Pose variants have unique total sizes, so determine counts from
			// that size rather than searching arbitrary position bytes for '#'.
			for (const array of layout.arrays) {
				readHeader(array.layout.id);
				const values = [];
				for (let i = 0; i < arrayCounts[array.name]; i++) {
					values.push(readRecord(array.layout));
				}
				result[array.name] = values;
			}
			return result;
		};
		readHeader(this.layout.id);
		const result = readRecord(this.layout);
		if (offset !== view.byteLength) throw new InvalidMultiplayerPacketError('Unexpected multiplayer payload');
		return result;
	}
}
